import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/nl";

import Navbar from "../Navbar/Navbar";
import ScaledCanvas from "./components/ScaledCanvas";
import TopbarClock from "./components/TopbarClock";
import TeamTargetHero from "./components/TeamTargetHero";
import MostSoldServices from "./components/MostSoldServices";
import MonthlyRankList from "./components/MonthlyRankList";
import YearRankList from "./components/YearRankList";

import AnimationLayer from "./animations/AnimationLayer";

import { useDashboardData } from "./hooks/useDashboardData";
import { useRankView } from "./hooks/useRankView";
import { useSessionGuard } from "./hooks/useSessionGuard";
import { useMonthRollover } from "./hooks/useMonthRollover";
import { useAnimations } from "./hooks/useAnimations";
import { useFrameWatch } from "./hooks/useRenderTier";
import { useSecretCode } from "./hooks/useSecretCode";
import { unlockLab } from "../AnimationLab/labAccess";
import { useSaleEvents } from "./hooks/useSaleEvents";
import { targetProgress } from "./utils/targetProgress";
import { testShortcuts } from "./constants";

import "./Dashboard.css";

// Het salesdashboard: het scherm dat de hele dag op de tv staat.
// Dit bestand zet alleen de onderdelen op hun plek; het echte werk zit in
// components/ (wat je ziet), hooks/ (wat er beweegt) en data/ (waar de
// cijfers vandaan komen).

const Dashboard = () => {
  // De maand die het dashboard toont staat in de URL, bijv. /dashboard/6/2025.
  const { month, year } = useParams();
  const monthName = dayjs(`${year}-${month}`).locale("nl").format("MMMM");
  const previousMonthName = dayjs(`${year}-${month}`)
    .subtract(1, "month")
    .locale("nl")
    .format("MMMM");

  // Verborgen doorgang naar het animatielab: typ "ja". Zie useSecretCode -
  // op het scherm staat hier niets over.
  const navigate = useNavigate();
  useSecretCode({
    ja: () => {
      unlockLab();
      navigate(`/animaties/${month}/${year}`);
    },
  });

  useSessionGuard();
  useMonthRollover(month, year);
  // Eén meting zodra het scherm staat: haalt dit apparaat de beelden niet,
  // dan zakt de kwaliteitstrap en tekent alles zich lichter opnieuw. Zie
  // utils/renderTier.js.
  useFrameWatch();
  const { data, isReady } = useDashboardData(month, year);
  const rankView = useRankView(data.rankList.length);

  // De animatielaag: useSaleEvents kijkt of er iets is bijgekomen en zet de
  // bijbehorende animatie aan. Met testShortcuts kun je ze op de tv ook
  // met de cijfertoetsen afspelen; alles los bekijken doe je op /animaties.
  const { active, playGroup, rootClass } = useAnimations({
    keyboard: testShortcuts,
  });
  useSaleEvents(isReady ? data : null, playGroup, `${month}-${year}`);

  if (!isReady) {
    return (
      <ScaledCanvas>
        <div className="dashboard-container">
          <Navbar />
          <div className="loading-container">
            <div className="spinner" />
          </div>
        </div>
      </ScaledCanvas>
    );
  }

  const progress = targetProgress({
    scored: data.scoredTowardsTarget,
    target: data.teamTarget.amount,
    month,
    year,
  });

  return (
    <ScaledCanvas>
      <div className={`dashboard-container ${rootClass}`}>
        <Navbar />
        <TopbarClock />
        <AnimationLayer active={active} />

        <div className="dash-body">
          <TeamTargetHero
            monthLabel={monthName.toUpperCase()}
            teamTarget={data.teamTarget}
            amount={data.amount}
            progress={progress}
          />

          <section className="dash-cols">
            <MostSoldServices
              soldServices={data.soldServices}
              monthLabel={monthName.toUpperCase()}
            />
            <MonthlyRankList
              rankList={data.rankList}
              rankView={rankView}
              now={monthName}
              bestSeller={data.bestSeller}
              previousMonth={previousMonthName}
              deals={data.deals}
              existantDeals={data.existantDeals}
            />
            <YearRankList yearRankList={data.yearRankList} />
          </section>
        </div>
      </div>
    </ScaledCanvas>
  );
};

export default Dashboard;
